<template>
    <div class="p-dropdown" :class="[className, { 'd-flex align-items-center': inline }]">
        <div
            v-if="labelText"
            class="d-flex align-items-center"
            :class="[{ 'mb-2': !inline }]"
        >
            <div
                v-if="required"
                class="required-dot mb-0"
            />
            <label
                :for="id"
                class="bold-label"
                v-bind="labelElProps"
            >
                {{ labelText }}
            </label>
        </div>
        <div :style="{ width: width }">
            <v-select
                v-model="selectedId"
                :options="formattedOptions"
                :reduce="taggable ? (item => item.shortName || item.id) : (company => company.id)"
                label="shortName"
                :clearable="clearable"
                :searchable="searchable"
                :taggable="taggable"
                :create-option="createNewOption"
                :placeholder="placeholder"
                @focus="handleFocus"
                @blur="handleBlur"
                @input="handleSelection"
            >
                <!-- Normal option template (not bold) -->
                <template #option="option">
                    <div>
                        <div>{{ option.shortName }}</div>
                        <div 
                            v-if="showAddress && option.addresses && option.addresses[0]" 
                            class="company-address-text"
                            style="font: italic normal normal 13px/15px Arial; color: #666; margin-top: 2px;"
                        >
                            {{ formatCompanyAddress(option) }}
                        </div>
                    </div>
                </template>

                <!-- Normal selected option template (not bold) -->
                <template #selected-option="option">
                    <span>{{ option.shortName }}</span>
                </template>
            </v-select>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Dropdown',
    inheritAttrs: false,
    props: {
        value: {
            type: [String, Number, null], // Accept different value types
            default: null
        },
        options: {
            type: Array,
            required: true
        },
        clearable: {
            type: Boolean,
            default: false
        },
        searchable: {
            type: Boolean,
            default: true
        },
        taggable: {
            type: Boolean,
            default: false
        },
        labelText: {
            type: String,
            default: ''
        },
        required: {
            type: Boolean,
            default: false
        },
        id: {
            type: String,
            default: ''
        },
        className: {
            type: String,
            default: ''
        },
        labelElProps: {
            type: Object,
            default: null
        },
        inline: {
            type: Boolean,
            default: false
        },
        width: {
            type: String,
            default: '100%'
        },
        placeholder: {
            type: String,
            default: ''
        },
        showAddress: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            previousSelection: null, // Store previous value
            isUserSelecting: false, // Track if user selects a new value
        };
    },
    computed: {
        selectedId: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit("input", newValue);
            },
        },
        formattedOptions() {
            return this.options.map(company => ({
                ...company,
                shortName: company.name.length > 50
                    ? company.name.substring(0, 50) + "..."
                    : company.name
            }));
        }
    },
    methods: {
        handleFocus() {
            this.previousSelection = this.selectedId; // Store old selection
            this.selectedId = null; // Clear selection on focus
            this.hasSelectedNewValue = false; // Reset selection tracking
        },
        handleBlur() {
            if (!this.hasSelectedNewValue) { // If no new selection was made
                this.selectedId = this.previousSelection; // Restore previous value
                this.$emit("input", this.previousSelection);
            }
        },
        handleSelection(newValue) {
            this.hasSelectedNewValue = true; // Mark that a new selection has been made
            this.previousSelection = newValue; // Update stored selection
        },
        createNewOption(inputValue) {
            // Check if the typed value already exists in options (case-insensitive)
            const existingOption = this.formattedOptions.find(option => 
                option.shortName.toLowerCase().trim() === inputValue.toLowerCase().trim()
            );
            
            // If option already exists, return the existing option instead of creating duplicate
            if (existingOption) {
                // Return the existing option so it gets selected properly
                return existingOption;
            }
            
            // If it's truly a new option, create a new object with proper structure
            return {
                id: inputValue, // Use the input value as ID for new options
                name: inputValue,
                shortName: inputValue
            };
        },
        formatCompanyAddress(company) {
            // Get the first address (index 0) from addresses array
            if (!company || !company.addresses || !company.addresses[0]) return '';
            
            const address = company.addresses[0];
            const parts = [];
            if (address.address_one) parts.push(address.address_one);
            if (address.city) parts.push(address.city);
            if (address.state) parts.push(address.state);
            
            return parts.join(', ');
        }
    }
}
</script>

<style lang="scss" scoped>
.p-dropdown {
    width: 100%;

    .bold-label {
        font: normal normal bold 14px / 16px Arial;;
    }

    ::v-deep {
        --vs-controls-color: rgba(0, 0, 0, 0.5);
        --vs-controls-size: .9;

        --vs-border-color: #E3E2E2;
        --vs-border-width: 1px;
        --vs-border-style: solid;
        --vs-border-radius: 0;

        --vs-dropdown-option-padding: 15px 10px;

        --vs-dropdown-option--active-bg: #E3E2E2;
        --vs-dropdown-option--active-color: #000000;


        .vs__dropdown-toggle {
            padding-top: 3px !important;
            padding-bottom: 5px !important;
        }

        .vs__selected-options {
            flex-wrap: nowrap;
        }

        .vs__selected {
            position: initial !important;
        }

        .vs__dropdown-menu {
            min-width: 0 !important;
        }
    }
}

.v-select {
    text-align: left;
    font: normal normal normal 13px/20px Arial;
    letter-spacing: 0px;
    color: #000000;
}

.required-dot {
    width: 4px;
    height: 4px;
    background-color: red;
    border-radius: 50%;
    display: inline-block;
    margin-right: 5px;
}
</style>
